'use client';

import { useState } from 'react';
import { PendingAction } from '@/lib/types';

export type Decision = 'allow' | 'deny' | 'allow-all';

interface ConfirmCardProps {
  action: PendingAction;
  onDecide: (decision: Decision) => void;
  busy?: boolean;
  /**
   * Owned by the transcript, not by this component. Local state would be lost whenever
   * React remounts the card, silently turning a settled decision back into a live prompt.
   */
  decided?: 'allow' | 'deny' | null;
  /** Settled with no click, because auto-approve was on. Worth saying so in the record. */
  auto?: boolean;
}

/**
 * The assistant asking permission, inline in the transcript.
 *
 * Deliberately not a modal: the request is part of the conversation, and once answered it
 * stays as a record of what was allowed and when.
 */
export default function ConfirmCard({ action, onDecide, busy, decided = null, auto }: ConfirmCardProps) {
  const [showArgs, setShowArgs] = useState(false);

  const decide = (decision: Decision) => {
    if (decided || busy) return;
    onDecide(decision);
  };

  if (decided) {
    const label = decided === 'deny' ? '✗ Denied' : auto ? '✓ Auto-allowed' : '✓ Allowed';
    return (
      <div className="text-[11px] px-2 py-1 rounded-lg bg-[var(--bg-elevated)] text-[var(--text-muted)]">
        {label} — {action.summary.replace(/\?$/, '')}
      </div>
    );
  }

  const danger = action.destructive;
  // "Generate 24 images?" -> pull the number out so it can carry the weight visually
  const batchCount = action.tool === 'run_batch' ? action.summary.match(/(\d+)\s+images?/)?.[1] : null;
  return (
    <div
      className={`rounded-xl border p-3 space-y-2 ${
        danger ? 'border-[var(--error)]/40 bg-red-500/5' : 'border-[var(--accent)]/40 bg-[var(--accent-soft)]/40'
      }`}
      data-confirm-card={action.tool}
    >
      {batchCount ? (
        <p className="text-sm font-medium text-[var(--text-primary)] flex items-baseline gap-1.5">
          <span className="text-2xl leading-none text-[var(--accent)]">{batchCount}</span>
          <span>images — generate them?</span>
        </p>
      ) : (
        <p className={`text-sm font-medium ${danger ? 'text-[var(--error)]' : 'text-[var(--text-primary)]'}`}>
          {danger ? '⚠ ' : ''}{action.summary}
        </p>
      )}

      {action.detail.length > 0 && (
        <ul className="text-xs text-[var(--text-muted)] space-y-0.5">
          {action.detail.map((d, i) => (
            <li key={i} className="break-words">{d}</li>
          ))}
        </ul>
      )}

      {showArgs && (
        <pre className="text-[10px] bg-[var(--bg-primary)] rounded p-2 overflow-x-auto max-h-40 text-[var(--text-muted)]">
          {JSON.stringify(action.args, null, 2)}
        </pre>
      )}

      <div className="flex items-center gap-2">
        {/* Destructive actions put Deny first, so the safe answer is the one under the cursor */}
        {danger ? (
          <>
            <button onClick={() => decide('deny')} disabled={busy}
              className="btn btn-primary px-3 py-1 text-xs disabled:opacity-40">Deny</button>
            <button onClick={() => decide('allow')} disabled={busy}
              className="btn btn-ghost px-3 py-1 text-xs text-[var(--error)] disabled:opacity-40">Allow</button>
          </>
        ) : (
          <>
            <button onClick={() => decide('allow')} disabled={busy}
              className="btn btn-primary px-3 py-1 text-xs disabled:opacity-40">Allow</button>
            <button onClick={() => decide('deny')} disabled={busy}
              className="btn btn-ghost px-3 py-1 text-xs disabled:opacity-40">Deny</button>
            {/* Deliberately absent on destructive cards: those are the ones the gate is
                for, and they keep asking even while auto-approve is on. */}
            <button onClick={() => decide('allow-all')} disabled={busy}
              data-allow-all
              title="Allow this and everything after it, until you clear the chat or reload"
              className="btn btn-ghost px-3 py-1 text-xs disabled:opacity-40">Always</button>
          </>
        )}
        <button
          onClick={() => setShowArgs((v) => !v)}
          className="ml-auto text-[10px] text-[var(--text-muted)] hover:underline cursor-pointer"
        >
          {showArgs ? 'hide details' : 'show details'}
        </button>
      </div>
    </div>
  );
}
